import { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, X as XIcon, Download, FileText, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtBytes = (n) => {
  if (!n) return '';
  if (n < 1024) return `${n}B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)}KB`;
  return `${(n / 1024 / 1024).toFixed(1)}MB`;
};

const photoUrl = (p) => (p?.url?.startsWith('http') ? p.url : `${BACKEND_URL}${p?.url || ''}`);

// Reusable lightbox/carousel for spot_photos. Pass an array + start index;
// optionally enable note editing and delete. Closes on Escape; arrow keys
// navigate.
export default function PhotoLightbox({
  photos,
  index,
  onClose,
  onPrev,
  onNext,
  canEditNote = false,
  onSaveNote,
  canDelete = false,
  onDelete,
}) {
  const photo = photos[index];
  const [draftNote, setDraftNote] = useState(photo?.note || '');
  const [savingNote, setSavingNote] = useState(false);

  useEffect(() => {
    setDraftNote(photo?.note || '');
  }, [photo?.id, photo?.note]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') onPrev();
      else if (e.key === 'ArrowRight') onNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, onPrev, onNext]);

  if (!photo) return null;

  const saveNote = async () => {
    if (savingNote || !onSaveNote) return;
    setSavingNote(true);
    try {
      await onSaveNote(photo, draftNote);
    } finally {
      setSavingNote(false);
    }
  };

  const dirty = (draftNote || '') !== (photo.note || '');

  return (
    <div className="fixed inset-0 bg-black/90 z-[100] flex items-center justify-center" onClick={onClose}>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        className="absolute top-3 right-3 text-white/80 hover:text-white p-2"
      >
        <XIcon className="w-6 h-6" />
      </button>

      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onPrev(); }}
        disabled={index === 0}
        className="absolute left-3 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 disabled:opacity-30 text-white rounded-full p-3"
      >
        <ChevronLeft className="w-6 h-6" />
      </button>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onNext(); }}
        disabled={index === photos.length - 1}
        className="absolute right-3 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 disabled:opacity-30 text-white rounded-full p-3"
      >
        <ChevronRight className="w-6 h-6" />
      </button>

      <div className="max-w-[95vw] max-h-[95vh] flex flex-col items-center gap-3" onClick={(e) => e.stopPropagation()}>
        <img
          src={photoUrl(photo)}
          alt={photo.filename || ''}
          className="max-w-[95vw] max-h-[70vh] object-contain rounded shadow-2xl bg-black"
        />
        <div className="bg-white/95 backdrop-blur-sm rounded-lg p-3 max-w-2xl w-full">
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2 text-xs text-slate-700">
            <div>
              <span className="font-medium">{photo.photographer_name || '—'}</span>
              {photo.spot_name && <> · {photo.spot_name}</>}
              {photo.event_name && <> · {photo.event_name}</>}
            </div>
            <div className="flex items-center gap-2 text-slate-500">
              <span>{index + 1} / {photos.length}</span>
              <span>·</span>
              <span>{fmtBytes(photo.size_bytes)}</span>
              <a
                href={photoUrl(photo)}
                target="_blank"
                rel="noreferrer"
                download={photo.filename || true}
                className="ml-2 inline-flex items-center gap-1 text-emerald-600 hover:text-emerald-700"
              >
                <Download className="w-3.5 h-3.5" /> download
              </a>
              {canDelete && onDelete && (
                <button
                  type="button"
                  onClick={() => onDelete(photo)}
                  className="ml-2 text-red-600 hover:text-red-700"
                >
                  delete
                </button>
              )}
            </div>
          </div>

          <div className="flex items-start gap-2">
            <FileText className="w-4 h-4 text-slate-400 mt-2 flex-shrink-0" />
            {canEditNote && onSaveNote ? (
              <>
                <textarea
                  value={draftNote}
                  onChange={(e) => setDraftNote(e.target.value)}
                  rows={2}
                  placeholder="Add a note about this photo (e.g. 'crop to 16:9', 'best of the run')"
                  className="flex-1 text-sm border rounded px-2 py-1 resize-none"
                />
                <Button size="sm" onClick={saveNote} disabled={!dirty || savingNote}>
                  <Save className="w-3.5 h-3.5 mr-1" />
                  {savingNote ? '…' : 'Save'}
                </Button>
              </>
            ) : (
              <p className="flex-1 text-sm text-slate-700 italic">
                {photo.note || 'No note.'}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
